import { useTranslation } from "react-i18next"

import type {
  FairUseThresholdId,
  FairUseThresholdIssue,
  FairUseThresholdValues,
} from "./fair-use-settings-utils"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import {
  FAIR_USE_MAX_THRESHOLD_PERCENT,
  FAIR_USE_MIN_THRESHOLD_PERCENT,
  FAIR_USE_THRESHOLD_IDS,
  FAIR_USE_THRESHOLD_INPUT_IDS,
  fairUseThresholdMarkers,
} from "./fair-use-settings-utils"

type FairUseThresholdLadderProps = {
  values: FairUseThresholdValues
  issue: FairUseThresholdIssue
  onChange: (id: FairUseThresholdId, value: string) => void
}

/**
 * The four steps of fair use, drawn as one rail.
 *
 * The old panel showed them as four numbers in a row of identical boxes,
 * which said nothing about the only two things that matter: that they happen
 * in an order, and what each one does to the person spending the budget. A
 * ladder says both without being read — the markers sit where the numbers put
 * them, so an out-of-order step is visibly out of order, and each step's card
 * describes the OUTCOME ("Sending is paused until a top-up or the next
 * month") rather than the mechanism it is implemented by.
 *
 * The rail runs 0 → the largest step, so the last marker is always at the far
 * right and the scale is honest whatever the numbers are; see
 * {@link fairUseThresholdMarkers}. It is decoration for the cards below, which
 * carry the real labels and inputs, so the whole track is `aria-hidden` and a
 * screen reader gets the labelled fields alone rather than the same four
 * numbers twice.
 */
export function FairUseThresholdLadder({ values, issue, onChange }: FairUseThresholdLadderProps) {
  const { t } = useTranslation()
  const markers = fairUseThresholdMarkers(values)

  const copy: Record<FairUseThresholdId, { label: string; marker: string; outcome: string }> = {
    warn: {
      label: t("adminDashboard.fairUse.settings.limits.warnLabel", "Warn at"),
      marker: t("adminDashboard.fairUse.settings.limits.warnMarker", "Warn"),
      outcome: t(
        "adminDashboard.fairUse.settings.limits.warnOutcome",
        "The budget ring turns amber and the user gets a notice."
      ),
    },
    request: {
      label: t("adminDashboard.fairUse.settings.limits.requestLabel", "Offer “Request more” at"),
      marker: t("adminDashboard.fairUse.settings.limits.requestMarker", "Offer “Request more”"),
      outcome: t(
        "adminDashboard.fairUse.settings.limits.requestOutcome",
        "The user can ask you for a top-up from the composer."
      ),
    },
    fallback: {
      label: t("adminDashboard.fairUse.settings.limits.fallbackLabel", "Fallback model at"),
      marker: t("adminDashboard.fairUse.settings.limits.fallbackMarker", "Fallback model"),
      outcome: t(
        "adminDashboard.fairUse.settings.limits.fallbackOutcome",
        "Answers switch to the fallback model below. The user keeps working."
      ),
    },
    stop: {
      label: t("adminDashboard.fairUse.settings.limits.stopLabel", "Stop at"),
      marker: t("adminDashboard.fairUse.settings.limits.stopMarker", "Stop"),
      outcome: t(
        "adminDashboard.fairUse.settings.limits.stopOutcome",
        "Sending is paused until a top-up or the next month."
      ),
    },
  }

  // Three bands, cut at the two steps that change what a turn costs: normal
  // service, warned, then the stop. The cuts come from the markers themselves,
  // so the colour under a marker is always the state that marker starts.
  const warnLeft = markers.find((marker) => marker.id === "warn")?.left ?? 0
  const fallbackLeft = markers.find((marker) => marker.id === "fallback")?.left ?? 100
  const railBackground =
    markers.length > 0
      ? `linear-gradient(90deg, hsl(var(--secondary)) 0 ${warnLeft}%, hsl(var(--warning)) ${warnLeft}% ${fallbackLeft}%, hsl(var(--destructive)) ${fallbackLeft}% 100%)`
      : undefined

  return (
    <div className="space-y-4">
      {markers.length > 0 && (
        <div aria-hidden="true" className="px-8 sm:px-10">
          <div className="relative h-20" data-testid="fair-use-threshold-rail">
            <div
              className="bg-muted absolute inset-x-0 top-14 h-2 rounded-full"
              style={railBackground ? { background: railBackground } : undefined}
            />
            {markers.map((marker) => (
              <div
                key={marker.id}
                className="absolute top-0 flex h-14 w-20 -translate-x-1/2 flex-col items-center justify-end gap-0.5"
                data-testid={`fair-use-threshold-marker-${marker.id}`}
                style={{ left: `${marker.left}%` }}
              >
                <span className="text-sm font-semibold tabular-nums">{marker.percent} %</span>
                <span className="text-muted-foreground text-center text-[11px] leading-tight">
                  {copy[marker.id].marker}
                </span>
                <span className="bg-foreground h-3.5 w-0.5 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FAIR_USE_THRESHOLD_IDS.map((id) => (
          <div key={id} className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-semibold" htmlFor={FAIR_USE_THRESHOLD_INPUT_IDS[id]}>
                {copy[id].label}
              </Label>
              <div className="flex shrink-0 items-center gap-1">
                <Input
                  className="h-10 w-16 text-right tabular-nums"
                  id={FAIR_USE_THRESHOLD_INPUT_IDS[id]}
                  inputMode="decimal"
                  type="text"
                  value={values[id]}
                  onChange={(event) => onChange(id, event.target.value)}
                />
                <span className="text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">{copy[id].outcome}</p>
          </div>
        ))}
      </div>

      {issue === "order" && (
        <p className="text-destructive text-xs" data-testid="fair-use-threshold-order-error">
          {t(
            "adminDashboard.fairUse.settings.limits.orderError",
            "Each step must be higher than the one before it: warn, request, fallback model, stop."
          )}
        </p>
      )}
      {issue === "range" && (
        <p className="text-destructive text-xs" data-testid="fair-use-threshold-range-error">
          {t("adminDashboard.fairUse.settings.limits.rangeError", {
            defaultValue:
              "Every step must be a percentage between {{min}} and {{max}}. Write decimals with a comma or a point (1,50) and no thousands grouping.",
            max: FAIR_USE_MAX_THRESHOLD_PERCENT,
            min: FAIR_USE_MIN_THRESHOLD_PERCENT,
          })}
        </p>
      )}
    </div>
  )
}
